import { useCallback, useContext } from 'react'
import { ContextShell } from '../../../modules/context'
import { DetailsSubscription, UserContext } from '../../../types'
import { useStripePortalUrl } from './use-stripe-portal-url'
import { useEbanxAutoRenew } from './use-ebanx-auto-renew'
import { useShellGate } from '../../iframe'

export type BillingProvider = 'stripe' | 'paypal' | 'ebanx' | 'revenuecat'

interface UseManageSubscription {
  loading: boolean
  openBillingPortal: (provider?: BillingProvider) => Promise<void>
}

const resolveProvider = (details?: DetailsSubscription): string | undefined => {
  return details?.providerName === 'revenuecat'
    ? details.providerGateway
    : details?.providerName
}

const getBillingProvider = (
  user: UserContext | null,
  provider?: BillingProvider
): string | undefined => {
  if (provider) {
    const subscription = user?.subscriptions?.find(
      ({ details }) => details.providerName === provider
    )

    return resolveProvider(subscription?.details)
  }

  return resolveProvider(user?.subscription?.details)
}

export const useManageSubscription = (): UseManageSubscription => {
  const { user } = useContext(ContextShell)
  const { loading: loadingStripePortalUrl, getStripePortalUrl } =
    useStripePortalUrl()
  const { loading: loadingEbanxAutoRenew, toggleAutoRenew } =
    useEbanxAutoRenew()
  const { isShellDisabled, sendMessage } = useShellGate()

  const openBillingPortal = useCallback(
    async (provider?: BillingProvider) => {
      const subscriptionProvider = getBillingProvider(user, provider)

      if (subscriptionProvider === 'stripe') {
        const stripePortalUrl = await getStripePortalUrl()

        if (stripePortalUrl) {
          if (isShellDisabled) {
            sendMessage({
              type: 'OPEN_EXTERNAL_URL',
              source: 'studio',
              payload: { url: stripePortalUrl }
            })
            return
          }

          window.location.href = stripePortalUrl
        }
      }

      if (subscriptionProvider === 'paypal') {
        const paypalUrl = 'https://paypal.com/myaccount/autopay/'

        if (isShellDisabled) {
          sendMessage({
            type: 'OPEN_EXTERNAL_URL',
            source: 'studio',
            payload: { url: paypalUrl }
          })
          return
        }

        window.open(paypalUrl, '_blank', 'noopener')
      }

      if (subscriptionProvider === 'ebanx') {
        await toggleAutoRenew()
      }

      if (subscriptionProvider === 'app_store') {
        if (isShellDisabled) {
          sendMessage({
            type: 'OPEN_EXTERNAL_URL',
            source: 'studio',
            payload: {
              url: 'https://account.apple.com/account/manage/section/subscriptions'
            }
          })
          return
        }

        window.open(
          'https://account.apple.com/account/manage/section/subscriptions',
          '_blank',
          'noopener'
        )
      }

      if (subscriptionProvider === 'play_store') {
        if (isShellDisabled) {
          sendMessage({
            type: 'OPEN_EXTERNAL_URL',
            source: 'studio',
            payload: { url: 'https://myaccount.google.com/subscriptions' }
          })
          return
        }

        window.open(
          'https://myaccount.google.com/subscriptions',
          '_blank',
          'noopener'
        )
      }
    },
    [user, getStripePortalUrl, toggleAutoRenew, isShellDisabled, sendMessage]
  )

  return {
    loading: loadingStripePortalUrl || loadingEbanxAutoRenew || false,
    openBillingPortal
  }
}
